import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import { DOCUMENT_COMPLIANCE_GATE_FLAG } from '#components/feature-flag/flags.js';
import { useFeatureFlag } from '#components/feature-flag/use-feature-flag.js';
import { useTRPC } from '#src/trpc.js';
import { lastOpenProjectAtom } from '#state/atoms/last-open-project-atom.js';
import { ComplianceBanner } from './compliance-banner.js';
import { DocumentComplianceAcceptDialog } from './document-compliance-accept-dialog.js';
import { DocumentComplianceBlockedScreen } from './document-compliance-blocked-screen.js';

import type { GetCurrentUserOutputDto, OrganizationDocumentStatusForUser } from '@cadstrom/backend/shared';

/**
 * Picks the compliance surface for the active organization: an acceptance dialog
 * when it is blocked and the user may accept, the blocked screen when they may
 * not, and the banner during grace.
 *
 * @returns The compliance surface, or null when nothing is pending.
 */
export function ActiveOrgComplianceGate() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const { orgName: routeOrgName } = useParams({ strict: false });
	const [lastOpenProject] = useAtom(lastOpenProjectAtom);
	const gateEnabled = useFeatureFlag(DOCUMENT_COMPLIANCE_GATE_FLAG) === true;

	// Non-throwing: a failed read renders nothing rather than erroring the app.
	const { data: currentUser } = useQuery(trpc.user.getCurrent.queryOptions(undefined, { throwOnError: false }));

	const activeOrgStatus = useMemo(
		() => resolveActiveOrgStatus(currentUser, routeOrgName ?? lastOpenProject.orgName),
		[currentUser, routeOrgName, lastOpenProject.orgName],
	);

	const acceptMutation = useMutation(
		trpc.documentCompliance.accept.mutationOptions({
			// The refetch is what dismisses the dialog.
			onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.user.getCurrent.queryKey() }),
		}),
	);

	// Guards on the documents, not `hasPendingDocuments`: an empty list would make
	// the dialog's all-accepted check vacuously true, enabling a submit that always
	// fails — with no way out, as the dialog cannot be dismissed.
	if (!gateEnabled || !activeOrgStatus || activeOrgStatus.pendingDocuments.length === 0) {
		return null;
	}

	if (!activeOrgStatus.isBlocked) {
		return (
			<ComplianceBanner
				organizationId={activeOrgStatus.organizationId}
				organizationName={activeOrgStatus.organizationName}
				pendingDocuments={activeOrgStatus.pendingDocuments}
				gracePeriodEnd={activeOrgStatus.gracePeriodEnd}
				canAccept={activeOrgStatus.canAccept}
			/>
		);
	}

	if (activeOrgStatus.canAccept) {
		return (
			<DocumentComplianceAcceptDialog
				// Remount per org: this gate is never unmounted, so without a key the
				// dialog carries ticked consent across an organization change.
				key={activeOrgStatus.organizationId}
				organizationName={activeOrgStatus.organizationName}
				pendingDocuments={activeOrgStatus.pendingDocuments}
				onAccept={() => acceptMutation.mutate(buildAcceptInput(activeOrgStatus))}
				submitting={acceptMutation.isPending}
				errorMessage={acceptMutation.error?.message ?? null}
			/>
		);
	}

	return (
		<DocumentComplianceBlockedScreen
			organizationName={activeOrgStatus.organizationName}
			pendingDocuments={activeOrgStatus.pendingDocuments}
		/>
	);
}

/**
 * Finds the compliance status for the organization the user is working in,
 * falling back to their first membership — the preferred name lives in
 * sessionStorage and is briefly absent on every fresh tab.
 *
 * @param currentUser - The loaded current user, or undefined while in flight.
 * @param preferredOrgName - Org username from the route or the last-open project.
 * @returns The matching status entry, or null when none can be resolved.
 */
function resolveActiveOrgStatus(
	currentUser: GetCurrentUserOutputDto | undefined,
	preferredOrgName: string | null | undefined,
): OrganizationDocumentStatusForUser | null {
	const documentStatus = currentUser?.documentStatus;
	if (!currentUser || !documentStatus) {
		return null;
	}

	const membership =
		currentUser.memberships.find((m) => m.groupUsername === preferredOrgName) ?? currentUser.memberships[0];
	if (!membership) {
		return null;
	}

	return documentStatus.find((s) => s.organizationId === membership.groupId) ?? null;
}

/**
 * Maps the pending documents onto the mutation's per-type fields, sending the
 * versions the user was shown so a mid-session registry change is rejected
 * rather than silently accepted.
 *
 * @param status - The active organization's compliance status.
 * @returns Input for `documentCompliance.accept`.
 */
function buildAcceptInput(status: OrganizationDocumentStatusForUser) {
	return {
		organizationId: status.organizationId,
		tosAcceptedId: status.pendingDocuments.find((d) => d.documentType === 'tos')?.versionId ?? null,
		privacyAcceptedId: status.pendingDocuments.find((d) => d.documentType === 'privacyPolicy')?.versionId ?? null,
	};
}
